import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fileUpload } from '@brand-lolly/app/_services/interceptors/timeout.interceptor';
import { smiUrl } from 'components';
import { Observable } from 'rxjs';

/**
 * A picture for the report.
 *
 * Sixteen charts is a document nobody reads twice, and every tool a reader
 * would otherwise reach for — gamma, Tome, Canva — puts imagery between the
 * numbers. This is that, generated from what the reader asked for plus what the
 * report is: its subject, who it is written for, the voice it is written in and
 * the colour the deck is painted in, all of which the backend reads from the
 * report itself. Or it is the reader's own, uploaded.
 *
 * `facts` is whatever this page has already resolved for the reader — it steers
 * the mood and never appears in the picture. The model is told at length not to
 * draw text, numbers or charts: the real chart is on the slide beside it, and
 * an image that contradicts it would be worse than no image.
 *
 * SLOW. An image model takes tens of seconds; show that it is working. And not
 * free: every drawing is a paid model call, which nothing meters yet (T-2536).
 */

/** The looks a reader can pick. Absent is the default mix, as before styles. */
export type ReportImageStyle = 'photo' | 'illustration' | '3d' | 'abstract';

/** Full width, or beside its caption on that side. Absent is full width. */
export type ReportImageAlign = 'full' | 'left' | 'right';

/** A third, half or two thirds of the width, beside the caption. */
export type ReportImageSize = 's' | 'm' | 'l';

/**
 * Which part of the picture its frame shows: the point kept in view, in
 * percent of the picture (CSS `object-position`), and how far in, about that
 * point. The same three numbers frame it in the editor, the PDF and the link.
 */
export interface ReportImageCrop {
	x: number;
	y: number;
	zoom: number;
}

/**
 * One picture, as the report stores it — the settings' `images` go to the
 * server as they are, so these are its names for them.
 *
 * Everything after `key` is optional, and absent on every picture saved before
 * it existed, which is exactly how those were drawn: generated, in the default
 * style, full width, centred, after the charts. Absent, never null: the server
 * reads a report back without its nulls, and a copy holding them would not
 * compare equal to the one it saved.
 */
export interface ReportImage {
	/** Stable id. Generating into the same slot replaces, never appends. */
	slot: string;
	/** What was asked for — the caption, and what the prompt was built from. */
	title: string;
	/** The S3 object key. Never point an <img> at it directly — see srcFor. */
	key: string;
	/** The look it was drawn in, so drawing it again keeps it. */
	style?: ReportImageStyle;
	/** Absent is generated. Only a generated picture is labelled as AI. */
	origin?: 'generated' | 'uploaded';
	/**
	 * What it sits straight after: `cover_page`, or a section's id. Absent is
	 * after the charts, at the end. A section that is not on the page takes its
	 * pictures to the end rather than hiding them.
	 */
	after?: string;
	align?: ReportImageAlign;
	size?: ReportImageSize;
	crop?: ReportImageCrop;
}

export interface ReportImageRequest {
	slot: string;
	heading: string;
	description?: string | null;
	facts?: string | null;
	aspect_ratio?: '16:9' | '3:2' | '4:3' | '1:1';
	style?: ReportImageStyle;
}

@Injectable({ providedIn: 'root' })
export class ReportImagesService {
	constructor(private http: HttpClient) {}

	/**
	 * Where to point an <img>.
	 *
	 * Through our own API, never at the bucket. The PDF export renders this
	 * page in a headless browser that Gotenberg hands a short-lived export
	 * token, and that token goes on EVERY request the browser makes — S3
	 * answers one carrying `Authorization: Bearer ...` with a 403, so a bucket
	 * URL is a blank page in the downloaded file and nothing says why.
	 */
	srcFor(image: ReportImage): string {
		const id = image.key.split('/').pop() ?? '';
		return smiUrl()
			.add('social-listening')
			.add('reports')
			.add('images')
			.add(id)
			.done();
	}

	generate(
		reportId: string,
		request: ReportImageRequest
	): Observable<ReportImage> {
		const url = smiUrl()
			.add('social-listening')
			.add('reports')
			.add(reportId)
			.add('images')
			.done();

		return this.http.post<ReportImage>(url, request);
	}

	/**
	 * The reader's own picture for the card in `slot`. The server refuses
	 * anything but a PNG, JPEG or WebP of up to 5 MB, and stores it re-encoded
	 * and marked as uploaded. Free: no model is called. Writes nothing — the
	 * card that comes back is kept by the report's own save, like a drawing.
	 */
	upload(
		reportId: string,
		file: File,
		card: { slot: string; title: string }
	): Observable<ReportImage> {
		const url = smiUrl()
			.add('social-listening')
			.add('reports')
			.add(reportId)
			.add('images')
			.add('upload')
			.done();

		const form = new FormData();
		form.append('file', file);
		form.append('slot', card.slot);
		form.append('title', card.title);
		return this.http.post<ReportImage>(url, form, {
			context: fileUpload(file.name)
		});
	}
}
